import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './CognitiveAgeCardV3.css';
import { getAppParams } from '../../apiService/timespam';
import brainIcon from '../../assets/img/brainlogo.png';
import ageStagesImg from '../../assets/img/evolution-residence-man-from-birth-old-age-stages.png';
import { APIV4_BASE_URL } from '../../apiService/apiClient';

const DEFAULT_TOTAL_GAMES = 18;
const MIN_GAMES_TO_UNLOCK = 10;
const BRAND_COLOR = '#35b400';
const RED = '#F85F3A';

// Same info-modal infographic used in V2 (brain + speed/accuracy/streak/variety icons).
const INFO_IMG_SRC = 'https://appcdn.goqii.com/storeimg/30886_1788784554.png';

// cogAge.cognitiveage / cogAge.actualage are formatted strings like "34y 2m 10d".
// Only years+months matter for the compact "1y 3m younger" line this version
// shows, so days are deliberately dropped.
const parseAgeToMonths = (str) => {
  const yMatch = /(\d+)\s*y/.exec(str || '');
  if (!yMatch) return null;
  const mMatch = /(\d+)\s*m(?!s)/.exec(str || '');
  return parseInt(yMatch[1], 10) * 12 + (mMatch ? parseInt(mMatch[1], 10) : 0);
};

const formatYearsMonths = (totalMonths) => {
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (years > 0 && months > 0) return `${years}y ${months}m`;
  if (years > 0) return `${years}y`;
  return `${months}m`;
};

// ─── Average-score ring -- same visual language as the per-game circles in
// GamesList.jsx (flat progress ring), just larger since it's the main focus
// of this version. ─────────────────────────────────────────────────────────
const RING_SIZE = 88;
const RING_STROKE = 8;
const RING_R = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R;

const ScoreRing = ({ score, target }) => {
  const hasScore = typeof score === 'number' && score > 0;
  const offset = hasScore
    ? RING_CIRCUMFERENCE - (Math.min(score, target) / target) * RING_CIRCUMFERENCE
    : RING_CIRCUMFERENCE;

  return (
    <div className="cac3-ring-wrap">
      <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} className="cac3-ring-svg">
        <circle
          cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_R}
          className="cac3-ring-track" strokeWidth={RING_STROKE} fill="none"
        />
        {hasScore && (
          <circle
            cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_R}
            className="cac3-ring-progress" strokeWidth={RING_STROKE} fill="none"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
          />
        )}
      </svg>
      <div className="cac3-ring-center">
        <span className="cac3-ring-score">{hasScore ? score : '--'}</span>
      </div>
    </div>
  );
};

// Purely decorative stand-in for the ring behind the locked glass card -- a
// fixed arc, never a real score. Blurred + aria-hidden, so it only gives the
// glass something to frost over.
const GhostRing = () => (
  <div className="cac3-ring-wrap">
    <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} className="cac3-ring-svg">
      <circle
        cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_R}
        className="cac3-ring-track" strokeWidth={RING_STROKE} fill="none"
      />
      <circle
        cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_R}
        className="cac3-ring-progress" strokeWidth={RING_STROKE} fill="none"
        strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={RING_CIRCUMFERENCE * 0.35}
        transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
      />
    </svg>
  </div>
);

const DiffArrow = ({ up }) => (
  <svg width="9" height="9" viewBox="0 0 10 10" style={{ transform: up ? 'none' : 'rotate(180deg)', flexShrink: 0 }}>
    <path d="M5 1 L9 8 L1 8 Z" fill="currentColor" />
  </svg>
);

/**
 * Cognitive Age card -- version 3. A big score ring on the left (the API's
 * own `current`/`target`), with the title and the age comparison ("N years
 * younger/older") stacked to its right. Self-contained like V1/V2 -- fetches
 * its own data, zero prop wiring needed. Separate file/component; V1 and V2
 * stay as they are, unused.
 */
const CognitiveAgeCardV3 = ({ onPlayNow }) => {
  const { t } = useTranslation();
  const [cogAge, setCogAge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const fetchCogAge = async () => {
      try {
        const { uid, goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType } = getAppParams();
        const body = new URLSearchParams({
          goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType,
          userId: uid,
        }).toString();
        const res = await fetch(`${APIV4_BASE_URL}/games/cognitive_age`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body,
        });
        const json = await res.json();
        if (json.code === 200) setCogAge(json.data || {});
      } catch (e) {
        console.error('cognitive_age fetch failed', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCogAge();
  }, []);

  const handlePlayNow = () => {
    setShowInfo(false);
    if (onPlayNow) onPlayNow();
  };

  if (loading) {
    return (
      <div className="cac3-card cac3-skeleton">
        <div className="cac3-skeleton-ring" />
        <div className="cac3-skeleton-lines">
          <div className="cac3-skeleton-line" style={{ width: '55%' }} />
          <div className="cac3-skeleton-line" style={{ width: '80%' }} />
          <div className="cac3-skeleton-line" style={{ width: '65%' }} />
        </div>
      </div>
    );
  }

  // API failed entirely -- fail quiet, rest of the page still works fine without this card
  if (!cogAge) return null;

  const totalGames = cogAge.totalGames || DEFAULT_TOTAL_GAMES;

  // Locked/unlocked is decided by the backend (`showcard`). The only line shown
  // while locked is "Play N more to unlock your Cognitive Age", with N worked
  // out here from the backend's `gamesPlayed`.
  const locked = cogAge.showcard === 'N';
  const gamesPlayed = cogAge.gamesPlayed || 0;
  const gamesLeft = Math.max(MIN_GAMES_TO_UNLOCK - gamesPlayed, 0);

  const actualMonths = !locked ? parseAgeToMonths(cogAge.actualage) : null;
  const cognitiveMonths = !locked ? parseAgeToMonths(cogAge.cognitiveage) : null;
  const hasBothMonths = actualMonths != null && cognitiveMonths != null;
  const diffMonths = hasBothMonths ? cognitiveMonths - actualMonths : 0;
  const isOlder = cogAge.difference ? cogAge.difference === 'older' : diffMonths > 0;
  const diffColor = isOlder ? RED : BRAND_COLOR;

  // "current" (== allScore.avgCognitiveScore) is the API's own 0-target score
  // for this card's ring -- already computed server-side, unlocked or not.
  const parsedCurrent = cogAge.current !== undefined && cogAge.current !== '' ? Number(cogAge.current) : NaN;
  const currentScore = Number.isFinite(parsedCurrent) ? parsedCurrent : null;
  const target = Number(cogAge.target) || 100;

  const infoButton = (
    <button
      type="button"
      className="cac3-info-btn"
      aria-label={t('games.cognitiveAge.infoAlt')}
      onClick={() => setShowInfo(true)}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <line x1="12" y1="11" x2="12" y2="16.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="7.5" r="1.15" fill="currentColor" />
      </svg>
    </button>
  );

  const titleRow = (
    <div className="cac3-title-row">
      <img src={brainIcon} alt="" className="cac3-title-icon" />
      <span className="cac3-title">{t('games.cognitiveAge.title')}</span>
    </div>
  );

  return (
    <>
      {locked ? (
        // Locked: no score, no age comparison -- just a frosted-glass placeholder
        // over a blurred, decorative ghost of the real card, with the single
        // "Play N more to unlock" line.
        <div className="cac3-card cac3-card--locked">
          {infoButton}

          <div className="cac3-ghost" aria-hidden="true">
            <span className="cac3-ghost-blob cac3-ghost-blob--a" />
            <span className="cac3-ghost-blob cac3-ghost-blob--b" />
            <div className="cac3-body">
              <GhostRing />
              <div className="cac3-info-col">
                <span className="cac3-ghost-line" style={{ width: '60%' }} />
                <span className="cac3-ghost-line" style={{ width: '90%' }} />
                <span className="cac3-ghost-line" style={{ width: '70%' }} />
              </div>
            </div>
          </div>

          <div className="cac3-glass">
            <span className="cac3-lock" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="5" y="11" width="14" height="10" rx="2.5" stroke="currentColor" strokeWidth="2" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="16" r="1.4" fill="currentColor" />
              </svg>
            </span>
            <div className="cac3-glass-text">
              {titleRow}
              <p className="cac3-locked-text">
                {gamesLeft > 0
                  ? t('games.cognitiveAge.lockedMore', { count: gamesLeft })
                  : t('games.cognitiveAge.lockedCalculating')}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="cac3-card">
          {infoButton}

          <div className="cac3-body">
            <ScoreRing score={currentScore} target={target} />

            <div className="cac3-info-col">
              {titleRow}

              {hasBothMonths && diffMonths !== 0 ? (
                <span className="cac3-diff-line" style={{ color: diffColor }}>
                  <DiffArrow up={isOlder} />
                  {t(isOlder ? 'games.cognitiveAge.diffOlder' : 'games.cognitiveAge.diffYounger', {
                    diff: formatYearsMonths(Math.abs(diffMonths)),
                  })}
                </span>
              ) : (
                <span className="cac3-diff-line cac3-diff-line--neutral">
                  {t('games.cognitiveAge.diffSame')}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {showInfo && (
        <div className="cac3-modal-overlay" role="dialog" aria-modal="true" onClick={() => setShowInfo(false)}>
          <div className="cac3-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cac3-modal-close" aria-label="Close" onClick={() => setShowInfo(false)}>×</button>

            <img src={brainIcon} alt="" className="cac3-modal-icon" />
            <h3 className="cac3-modal-title">{t('games.cognitiveAge.modalTitle')}</h3>

            <img src={INFO_IMG_SRC} alt={t('games.cognitiveAge.infoImgAlt')} className="cac3-modal-info-img" />
            <p className="cac3-modal-text">{t('games.cognitiveAge.modalCaption')}</p>

            <img src={ageStagesImg} alt={t('games.cognitiveAge.scaleImgAlt')} className="cac3-modal-scale-img" />

            <p className="cac3-modal-text">
              {t('games.cognitiveAge.modalText3', { minGames: MIN_GAMES_TO_UNLOCK, totalGames })}
            </p>
            <p className="cac3-modal-text">{t('games.cognitiveAge.modalText4')}</p>

            <button type="button" className="cac3-modal-cta" onClick={handlePlayNow}>
              {t('games.cognitiveAge.playNow')}
            </button>

            <p className="cac3-modal-disclaimer">{t('games.cognitiveAge.disclaimer')}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default CognitiveAgeCardV3;
